import { useCallback, useState, type MutableRefObject } from 'react';
import type { FormInstance } from 'antd';
import {
  expandBundleToItems,
  recalcBundleItemQuantities,
  findBundleItemIndices,
  type Bundle,
  type BundleGroupEntry,
  type InventoryType,
} from '@utils/orders/bundleExpansion';

interface UseOrderBundlesArgs {
  form: FormInstance;
  /**
   * Refs whose `.current` is the antd Form.List add/remove. The hook reads
   * them lazily, so callers can wire the refs inside Form.List's render prop
   * without breaking referential stability of the returned handlers.
   */
  addItemRef: MutableRefObject<((item: Record<string, unknown>) => void) | null>;
  removeItemRef: MutableRefObject<((index: number) => void) | null>;
  inventoryTypes: readonly InventoryType[];
}

interface UseOrderBundlesResult {
  bundleGroups: Record<string, BundleGroupEntry>;
  setBundleGroups: React.Dispatch<React.SetStateAction<Record<string, BundleGroupEntry>>>;
  /** Expand a bundle into per-product form items. */
  handleSelectBundle: (bundle: Bundle) => void;
  /** Recalc child quantities when the user changes the set count. */
  handleBundleSetQuantityChange: (bundleKey: string, newQuantity: number) => void;
  /** Remove every item belonging to the bundle. */
  handleRemoveBundle: (bundleKey: string, removeFormItem: (index: number) => void) => void;
  /**
   * Convenience for the case where the user picked a bundle from inside an
   * existing (empty) ProductItemCard: drop that card first, then expand.
   */
  handleBundleSelectFromCard: (bundle: Bundle, cardIndex: number) => void;
}

export function useOrderBundles({
  form,
  addItemRef,
  removeItemRef,
  inventoryTypes,
}: UseOrderBundlesArgs): UseOrderBundlesResult {
  const [bundleGroups, setBundleGroups] = useState<Record<string, BundleGroupEntry>>({});

  const handleSelectBundle = useCallback((bundle: Bundle) => {
    if (!addItemRef.current) return;
    const bundleKey = `bundle_${Date.now()}`;
    const { items, perSetQuantities } = expandBundleToItems(bundle, bundleKey, inventoryTypes);
    items.forEach(item => addItemRef.current!(item as unknown as Record<string, unknown>));

    setBundleGroups(prev => ({
      ...prev,
      [bundleKey]: { bundle, setQuantity: 1, perSetQuantities },
    }));
  }, [addItemRef, inventoryTypes]);

  const handleBundleSetQuantityChange = useCallback((bundleKey: string, newQuantity: number) => {
    setBundleGroups(prev => {
      const group = prev[bundleKey];
      if (!group) return prev;
      return { ...prev, [bundleKey]: { ...group, setQuantity: newQuantity } };
    });

    const items = (form.getFieldValue('order_items') || []) as Array<{
      bundle_key?: string;
      bundle_per_set_qty?: number;
      quantity?: number;
    }>;
    form.setFieldValue('order_items', recalcBundleItemQuantities(items, bundleKey, newQuantity));
  }, [form]);

  const handleRemoveBundle = useCallback((bundleKey: string, removeFormItem: (index: number) => void) => {
    const items = (form.getFieldValue('order_items') || []) as Array<{ bundle_key?: string }>;
    findBundleItemIndices(items, bundleKey).forEach(idx => removeFormItem(idx));

    setBundleGroups(prev => {
      const next = { ...prev };
      delete next[bundleKey];
      return next;
    });
  }, [form]);

  const handleBundleSelectFromCard = useCallback((bundle: Bundle, cardIndex: number) => {
    if (removeItemRef.current) {
      removeItemRef.current(cardIndex);
    }
    handleSelectBundle(bundle);
  }, [removeItemRef, handleSelectBundle]);

  return {
    bundleGroups,
    setBundleGroups,
    handleSelectBundle,
    handleBundleSetQuantityChange,
    handleRemoveBundle,
    handleBundleSelectFromCard,
  };
}
